import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Anchor, Plus, Trash2, Users, CalendarDays, Wallet, Wrench, ChevronLeft, ChevronRight, Upload, FileText, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";

const TYPE = {
  owner_use: { label: "Uso copropietario", dot: "bg-blue-500", chip: "bg-blue-50 text-blue-700" },
  rental: { label: "Alquiler", dot: "bg-[#C2A878]", chip: "bg-[#C2A878]/15 text-[#8a713f]" },
  maintenance: { label: "Mantenimiento", dot: "bg-red-500", chip: "bg-red-50 text-red-700" },
  cleaning: { label: "Limpieza", dot: "bg-teal-500", chip: "bg-teal-50 text-teal-700" },
  service: { label: "Servicio", dot: "bg-purple-500", chip: "bg-purple-50 text-purple-700" },
  blocked: { label: "No disponible", dot: "bg-slate-400", chip: "bg-slate-100 text-slate-600" },
};
const CATLABEL = { maintenance: "Mantenimiento", cleaning: "Limpieza", service: "Servicios", mooring: "Amarre", insurance: "Seguro", revision: "Revisiones", repair: "Reparaciones", other: "Otros" };
const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const MEMBER_COLORS = ["#2563eb", "#0d9488", "#7c3aed", "#db2777", "#0891b2", "#65a30d"];
const eur = (n) => `€${Number(n || 0).toLocaleString("es-ES")}`;
const fmtRange = (a, b) => { const d = (s) => new Date(s).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }); return b && b !== a ? `${d(a)} – ${d(b)}` : d(a); };
const monthOf = (s) => (s ? parseInt(s.slice(5, 7), 10) - 1 : 0);

export default function Management() {
  const { user } = useAuth();
  const [boats, setBoats] = useState(null);
  const [sel, setSel] = useState(null);
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState({ members: [], role: "coowner" });
  const [expenses, setExpenses] = useState([]);
  const [docs, setDocs] = useState([]);
  const [tab, setTab] = useState("resumen");

  useEffect(() => { api.get("/management/boats").then((r) => { setBoats(r.data); if (r.data[0]) setSel(r.data[0].id); }).catch(() => setBoats([])); }, []);
  const load = (id) => {
    api.get(`/management/boats/${id}/summary`).then((r) => setSummary(r.data)).catch(() => {});
    api.get(`/management/boats/${id}/events`).then((r) => setEvents(r.data)).catch(() => {});
    api.get(`/management/boats/${id}/members`).then((r) => setMembers(r.data)).catch(() => {});
    api.get(`/management/boats/${id}/expenses`).then((r) => setExpenses(r.data)).catch(() => {});
    api.get(`/management/boats/${id}/documents`).then((r) => setDocs(r.data)).catch(() => {});
  };
  useEffect(() => { if (sel) load(sel); }, [sel]);

  const memberColor = useMemo(() => {
    const map = {}; let i = 0;
    (members.members || []).forEach((m) => { if (m.user_id) { map[m.user_id] = MEMBER_COLORS[i % MEMBER_COLORS.length]; i++; } });
    return map;
  }, [members]);

  if (boats === null) return <div className="max-w-6xl mx-auto px-6 py-20 text-slate-400">Cargando…</div>;
  if (boats.length === 0) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="management-empty">
      <Anchor className="h-8 w-8 text-slate-300 mx-auto" />
      <h1 className="font-serif text-3xl text-[#0B192C] mt-6">Propietario · Gestión</h1>
      <p className="mt-4 text-slate-500 leading-relaxed">Aún no tienes acceso a la gestión de ninguna embarcación. Aparecerá aquí cuando Bevela valide tu perfil de propietario y tu relación con un barco.</p>
    </div>
  );

  const isAdmin = members.role === "owner_admin";
  const s = summary;
  const TABS = [["resumen", "Resumen"], ["calendario", "Calendario"], ["uso", "Uso"], ["economia", "Economía"], ["alquileres", "Alquileres"], ["costes", "Costes"], ["mantenimiento", "Mantenimiento"], ["limpieza", "Limpieza"], ["copropietarios", "Copropietarios"], ["documentacion", "Documentación"]];

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="management-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Propietario · Gestión</p>
          <h1 className="font-serif text-3xl sm:text-4xl mt-2 text-[#0B192C]" data-testid="management-boat-name">{s?.boat?.name}</h1>
          <p className="mt-1 text-slate-500 text-sm">{s?.boat?.zone}{s?.boat?.port ? ` · ${s.boat.port}` : ""} · <span className="uppercase tracking-wide text-xs">{s?.boat?.status}</span></p>
        </div>
        {boats.length > 1 && (
          <select value={sel} onChange={(e) => setSel(e.target.value)} className="input-elegant max-w-xs" data-testid="management-boat-select">
            {boats.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>

      {s && (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="management-summary">
          <Card icon={<CalendarDays className="h-4 w-4" />} title={`Uso ${s.year}`}>
            <Row k="Mis días" v={`${s.usage.mine}`} /><Row k="Otros copropietarios" v={`${s.usage.others}`} />
            <Row k="Alquiler" v={`${s.usage.rental}`} /><Row k="Total" v={`${s.usage.total}`} strong />
          </Card>
          <Card icon={<Wallet className="h-4 w-4" />} title={`Economía ${s.year}`}>
            <Row k="Ingresos" v={eur(s.economy.income)} /><Row k="Costes" v={`− ${eur(s.economy.costs)}`} />
            <Row k="Resultado" v={eur(s.economy.result)} strong />
          </Card>
          <Card icon={<Wrench className="h-4 w-4" />} title="Mantenimiento">
            {s.maintenance.next ? (<><p className="font-serif text-lg text-[#0B192C]">{s.maintenance.next.title}</p><p className="text-sm text-slate-500 mt-1">{fmtRange(s.maintenance.next.start_date, s.maintenance.next.end_date)}</p></>) : <p className="text-sm text-slate-400">Sin próximos servicios</p>}
          </Card>
          <Card icon={<Users className="h-4 w-4" />} title="Copropietarios">
            <p className="font-serif text-3xl text-[#0B192C]">{s.member_count}</p><p className="text-sm text-slate-500 mt-1">{s.upcoming.length} próximos eventos</p>
          </Card>
        </div>
      )}

      <div className="mt-10 flex gap-1 border-b border-slate-100 overflow-x-auto" data-testid="management-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm font-display -mb-px border-b-2 whitespace-nowrap transition-colors ${tab === k ? "border-[#0B192C] text-[#0B192C]" : "border-transparent text-slate-400 hover:text-[#0B192C]"}`} data-testid={`management-tab-${k}`}>{l}</button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "resumen" && <ResumenUpcoming s={s} memberColor={memberColor} />}
        {tab === "calendario" && <Calendar boatId={sel} events={events} onChange={() => load(sel)} year={s?.year} memberColor={memberColor} />}
        {tab === "uso" && <Usage events={events} members={members.members} year={s?.year} memberColor={memberColor} />}
        {tab === "economia" && <Economy events={events} expenses={expenses} year={s?.year} economy={s?.economy} />}
        {tab === "alquileres" && <Rentals events={events} />}
        {tab === "costes" && <Costs events={events} expenses={expenses} />}
        {tab === "mantenimiento" && <ServiceList boatId={sel} events={events} type="maintenance" onChange={() => load(sel)} />}
        {tab === "limpieza" && <ServiceList boatId={sel} events={events} type="cleaning" onChange={() => load(sel)} />}
        {tab === "copropietarios" && <CoOwners boatId={sel} data={members} isAdmin={isAdmin} onChange={() => load(sel)} />}
        {tab === "documentacion" && <Documents boatId={sel} docs={docs} onChange={() => load(sel)} />}
      </div>
    </div>
  );
}

const Card = ({ icon, title, children }) => (<div className="card-clean p-5"><p className="flex items-center gap-2 text-[10px] font-display uppercase tracking-widest text-slate-400">{icon}{title}</p><div className="mt-3 space-y-1.5">{children}</div></div>);
const Row = ({ k, v, strong }) => (<div className="flex items-center justify-between text-sm"><span className="text-slate-500">{k}</span><span className={strong ? "font-serif text-lg text-[#0B192C]" : "text-[#0B192C]"}>{v}</span></div>);

function ResumenUpcoming({ s, memberColor }) {
  if (!s) return null;
  return (
    <div data-testid="management-resumen">
      <p className="eyebrow">Próximos eventos, salidas y mantenimientos</p>
      {s.upcoming.length === 0 && <p className="mt-4 text-slate-400 text-sm">No hay nada programado próximamente.</p>}
      <div className="mt-5 space-y-2">
        {s.upcoming.map((e) => (
          <div key={e.id} className="flex items-center gap-4 rounded-xl border border-slate-100 p-4">
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: e.type === "owner_use" ? (memberColor[e.member_id] || "#2563eb") : undefined }}>
              {e.type !== "owner_use" && <span className={`block h-2.5 w-2.5 rounded-full ${TYPE[e.type]?.dot}`} />}
            </span>
            <div className="flex-1 min-w-0"><p className="text-[#0B192C]">{e.title || TYPE[e.type]?.label}</p><p className="text-xs text-slate-400">{fmtRange(e.start_date, e.end_date)}{e.member_name ? ` · ${e.member_name}` : ""}</p></div>
            <span className={`text-[11px] px-2 py-1 rounded-full ${TYPE[e.type]?.chip}`}>{TYPE[e.type]?.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MonthGrid({ events, year, memberColor }) {
  const [month, setMonth] = useState(new Date().getMonth());
  const first = new Date(year, month, 1);
  const startCol = (first.getDay() + 6) % 7; // Mon-based
  const days = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startCol; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  const evForDay = (d) => {
    const ds = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    return events.filter((e) => e.start_date <= ds && (e.end_date || e.start_date) >= ds);
  };
  return (
    <div data-testid="management-month-grid">
      <div className="flex items-center justify-between mb-3">
        <p className="font-serif text-xl text-[#0B192C]">{MONTHS[month]} {year}</p>
        <div className="flex gap-1">
          <button onClick={() => setMonth((m) => (m + 11) % 12)} className="p-2 rounded-full hover:bg-slate-100" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => setMonth((m) => (m + 1) % 12)} className="p-2 rounded-full hover:bg-slate-100" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-display uppercase tracking-widest text-slate-400 mb-1">
        {["L", "M", "X", "J", "V", "S", "D"].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const evs = evForDay(d);
          return (
            <div key={i} className="aspect-square rounded-lg border border-slate-100 p-1 text-left relative">
              <span className="text-[11px] text-slate-500">{d}</span>
              <div className="flex flex-wrap gap-0.5 mt-0.5">
                {evs.slice(0, 4).map((e, j) => (
                  <span key={j} title={`${TYPE[e.type]?.label}${e.member_name ? " · " + e.member_name : ""}`} className={`h-1.5 w-1.5 rounded-full ${e.type === "owner_use" ? "" : TYPE[e.type]?.dot}`} style={e.type === "owner_use" ? { background: memberColor[e.member_id] || "#2563eb" } : undefined} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Calendar({ boatId, events, onChange, year, memberColor }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: "owner_use", title: "", start_date: "", end_date: "", provider: "", cost: "", income: "", commission: "" });
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));
  const submit = async () => {
    if (!f.start_date) return toast.error("Indica la fecha de inicio");
    const p = { type: f.type, title: f.title, start_date: f.start_date, end_date: f.end_date || f.start_date, provider: f.provider, notes: "" };
    if (f.cost) p.cost = Number(f.cost); if (f.income) p.income = Number(f.income); if (f.commission) p.commission = Number(f.commission);
    try { await api.post(`/management/boats/${boatId}/events`, p); toast.success("Evento añadido"); setOpen(false); setF({ type: "owner_use", title: "", start_date: "", end_date: "", provider: "", cost: "", income: "", commission: "" }); onChange(); }
    catch { toast.error("No se pudo crear el evento"); }
  };
  const del = async (id) => { await api.delete(`/management/boats/${boatId}/events/${id}`); onChange(); };
  const showCost = ["maintenance", "cleaning", "service"].includes(f.type);
  const showIncome = f.type === "rental";

  return (
    <div data-testid="management-calendar">
      <div className="card-clean p-5 mb-6"><MonthGrid events={events} year={year || new Date().getFullYear()} memberColor={memberColor} /></div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex flex-wrap gap-2">{Object.entries(TYPE).map(([k, v]) => (<span key={k} className={`text-[11px] px-2 py-1 rounded-full inline-flex items-center gap-1.5 ${v.chip}`}><span className={`h-1.5 w-1.5 rounded-full ${v.dot}`} />{v.label}</span>))}</div>
        <button onClick={() => setOpen((v) => !v)} className="btn-outline text-sm" data-testid="management-add-event"><Plus className="h-4 w-4" /> Añadir</button>
      </div>
      {open && (
        <div className="card-clean p-5 mb-5" data-testid="management-event-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="label-elegant">Tipo</label><select className="input-elegant" value={f.type} onChange={set("type")} data-testid="event-type">{Object.entries(TYPE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></div>
            <div><label className="label-elegant">Título</label><input className="input-elegant" value={f.title} onChange={set("title")} data-testid="event-title" /></div>
            <div><label className="label-elegant">Desde</label><input type="date" className="input-elegant" value={f.start_date} onChange={set("start_date")} data-testid="event-start" /></div>
            <div><label className="label-elegant">Hasta</label><input type="date" className="input-elegant" value={f.end_date} onChange={set("end_date")} data-testid="event-end" /></div>
            {showCost && <div><label className="label-elegant">Proveedor</label><input className="input-elegant" value={f.provider} onChange={set("provider")} data-testid="event-provider" /></div>}
            {showCost && <div><label className="label-elegant">Coste (€)</label><input type="number" className="input-elegant" value={f.cost} onChange={set("cost")} data-testid="event-cost" /></div>}
            {showIncome && <div><label className="label-elegant">Ingreso bruto (€)</label><input type="number" className="input-elegant" value={f.income} onChange={set("income")} data-testid="event-income" /></div>}
            {showIncome && <div><label className="label-elegant">Comisión (€)</label><input type="number" className="input-elegant" value={f.commission} onChange={set("commission")} data-testid="event-commission" /></div>}
          </div>
          <button onClick={submit} className="btn-primary text-sm mt-4" data-testid="event-submit">Guardar evento</button>
        </div>
      )}
      <p className="eyebrow mb-3">Historial</p>
      {events.length === 0 && <p className="text-slate-400 text-sm">Sin eventos registrados.</p>}
      <div className="space-y-2">
        {events.map((e) => (
          <div key={e.id} className="flex items-center gap-4 rounded-xl border border-slate-100 p-4" data-testid={`management-event-${e.id}`}>
            <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${e.type === "owner_use" ? "" : TYPE[e.type]?.dot}`} style={e.type === "owner_use" ? { background: memberColor[e.member_id] || "#2563eb" } : undefined} />
            <div className="min-w-0 flex-1"><p className="text-[#0B192C]">{e.title || TYPE[e.type]?.label}</p><p className="text-xs text-slate-400">{fmtRange(e.start_date, e.end_date)} · {e.days} día(s){e.member_name ? ` · ${e.member_name}` : ""}{e.provider ? ` · ${e.provider}` : ""}</p></div>
            <span className={`text-[11px] px-2 py-1 rounded-full ${TYPE[e.type]?.chip}`}>{TYPE[e.type]?.label}</span>
            {e.income ? <span className="text-sm text-emerald-700">{eur(e.income)}</span> : null}
            {e.cost ? <span className="text-sm text-red-600">− {eur(e.cost)}</span> : null}
            <button onClick={() => del(e.id)} className="text-slate-300 hover:text-red-500" data-testid={`event-delete-${e.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Usage({ events, members, year, memberColor }) {
  const rows = useMemo(() => {
    const map = {};
    members.forEach((m) => { if (m.user_id) map[m.user_id] = { name: m.name, days: 0, color: memberColor[m.user_id] }; });
    events.filter((e) => e.type === "owner_use").forEach((e) => { const k = e.member_id; if (!map[k]) map[k] = { name: e.member_name || "Copropietario", days: 0 }; map[k].days += e.days || 0; });
    return Object.values(map);
  }, [events, members, memberColor]);
  const totalOwners = rows.reduce((a, r) => a + r.days, 0);
  const rentalDays = events.filter((e) => e.type === "rental").reduce((a, e) => a + (e.days || 0), 0);
  return (
    <div data-testid="management-usage">
      <p className="eyebrow">Uso {year} por copropietario</p>
      <div className="mt-5 rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FA] text-slate-400 text-xs uppercase tracking-wide"><tr><th className="text-left px-5 py-3">Copropietario</th><th className="text-right px-5 py-3">Días utilizados</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (<tr key={i} className="border-t border-slate-100" data-testid={`usage-row-${i}`}><td className="px-5 py-3 text-[#0B192C] flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: r.color || "#94a3b8" }} />{r.name}</td><td className="px-5 py-3 text-right text-[#0B192C]">{r.days}</td></tr>))}
            <tr className="border-t border-slate-200 bg-[#F8F9FA]"><td className="px-5 py-3 font-display text-[#0B192C]">Total copropietarios</td><td className="px-5 py-3 text-right font-serif text-lg text-[#0B192C]">{totalOwners}</td></tr>
            <tr className="border-t border-slate-100"><td className="px-5 py-3 text-slate-500">Alquiler</td><td className="px-5 py-3 text-right text-[#0B192C]">{rentalDays}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Economy({ events, expenses, year, economy }) {
  const data = useMemo(() => {
    const inc = Array(12).fill(0), cost = Array(12).fill(0);
    events.forEach((e) => { const m = monthOf(e.start_date); if (e.type === "rental" && e.income) inc[m] += Number(e.income); if (e.cost) cost[m] += Number(e.cost); });
    expenses.forEach((x) => { cost[monthOf(x.date)] += Number(x.amount || 0); });
    return MONTHS.map((name, i) => ({ name, Ingresos: Math.round(inc[i]), Costes: Math.round(cost[i]) }));
  }, [events, expenses]);
  return (
    <div data-testid="management-economy">
      <div className="grid sm:grid-cols-3 gap-5 mb-8">
        <div className="card-clean p-5"><p className="eyebrow">Ingresos {year}</p><p className="font-serif text-3xl text-emerald-700 mt-2">{eur(economy?.income)}</p></div>
        <div className="card-clean p-5"><p className="eyebrow">Costes {year}</p><p className="font-serif text-3xl text-red-600 mt-2">− {eur(economy?.costs)}</p></div>
        <div className="card-clean p-5"><p className="eyebrow">Resultado {year}</p><p className="font-serif text-3xl text-[#0B192C] mt-2">{eur(economy?.result)}</p></div>
      </div>
      <p className="eyebrow mb-4">Ingresos vs Costes por mes</p>
      <div className="card-clean p-5" style={{ height: 300 }} data-testid="economy-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={50} />
            <Tooltip formatter={(v) => eur(v)} contentStyle={{ borderRadius: 12, border: "1px solid #eef2f6" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Ingresos" fill="#0B7A55" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Costes" fill="#C2A878" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Rentals({ events }) {
  const rentals = events.filter((e) => e.type === "rental");
  const gross = rentals.reduce((a, e) => a + Number(e.income || 0), 0);
  const comm = rentals.reduce((a, e) => a + Number(e.commission || 0), 0);
  const days = rentals.reduce((a, e) => a + (e.days || 0), 0);
  return (
    <div data-testid="management-rentals">
      <div className="grid sm:grid-cols-4 gap-4 mb-6">
        <div className="card-clean p-4"><p className="eyebrow">Días alquilados</p><p className="font-serif text-2xl text-[#0B192C] mt-1">{days}</p></div>
        <div className="card-clean p-4"><p className="eyebrow">Alquileres</p><p className="font-serif text-2xl text-[#0B192C] mt-1">{rentals.length}</p></div>
        <div className="card-clean p-4"><p className="eyebrow">Ingresos brutos</p><p className="font-serif text-2xl text-emerald-700 mt-1">{eur(gross)}</p></div>
        <div className="card-clean p-4"><p className="eyebrow">Ingresos netos</p><p className="font-serif text-2xl text-[#0B192C] mt-1">{eur(gross - comm)}</p></div>
      </div>
      <div className="rounded-lg bg-[#0B192C]/5 px-4 py-3 text-xs text-slate-500 mb-6">Próximamente los alquileres entrarán automáticamente desde la pestaña <b>Alquila</b> de Explora e se imputarán a esta cuenta.</div>
      {rentals.length === 0 && <p className="text-slate-400 text-sm">Sin alquileres registrados.</p>}
      <div className="space-y-2">
        {rentals.map((e) => (
          <div key={e.id} className="flex items-center justify-between rounded-xl border border-slate-100 p-4" data-testid={`rental-${e.id}`}>
            <div><p className="text-[#0B192C]">{e.title || "Alquiler"}</p><p className="text-xs text-slate-400">{fmtRange(e.start_date, e.end_date)} · {e.days} día(s)</p></div>
            <div className="text-right"><p className="text-emerald-700 text-sm">{eur(e.income)}</p>{e.commission ? <p className="text-xs text-slate-400">comisión − {eur(e.commission)}</p> : null}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Costs({ events, expenses }) {
  const groups = useMemo(() => {
    const g = {};
    events.forEach((e) => { if (e.cost) { const c = e.type; g[c] = (g[c] || 0) + Number(e.cost); } });
    expenses.forEach((x) => { g[x.category] = (g[x.category] || 0) + Number(x.amount || 0); });
    return Object.entries(g).sort((a, b) => b[1] - a[1]);
  }, [events, expenses]);
  const total = groups.reduce((a, [, v]) => a + v, 0);
  return (
    <div data-testid="management-costs">
      <p className="eyebrow">Costes del año por categoría</p>
      <div className="mt-5 rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full text-sm">
          <tbody>
            {groups.map(([cat, v]) => (<tr key={cat} className="border-b border-slate-100" data-testid={`cost-${cat}`}><td className="px-5 py-3 text-[#0B192C]">{CATLABEL[cat] || cat}</td><td className="px-5 py-3 text-right text-[#0B192C]">{eur(v)}</td></tr>))}
            <tr className="bg-[#F8F9FA]"><td className="px-5 py-3 font-display text-[#0B192C]">Coste total anual</td><td className="px-5 py-3 text-right font-serif text-lg text-[#0B192C]">{eur(total)}</td></tr>
          </tbody>
        </table>
      </div>
      {groups.length === 0 && <p className="text-slate-400 text-sm mt-4">Sin costes registrados.</p>}
    </div>
  );
}

function ServiceList({ boatId, events, type, onChange }) {
  const list = events.filter((e) => e.type === type);
  const today = new Date().toISOString().slice(0, 10);
  const next = list.filter((e) => e.start_date >= today).sort((a, b) => a.start_date.localeCompare(b.start_date));
  const done = list.filter((e) => e.start_date < today).sort((a, b) => b.start_date.localeCompare(a.start_date));
  const label = type === "maintenance" ? "mantenimiento" : "limpieza";
  const del = async (id) => { await api.delete(`/management/boats/${boatId}/events/${id}`); onChange(); };
  const Item = ({ e }) => (
    <div className="flex items-center justify-between rounded-xl border border-slate-100 p-4" data-testid={`${type}-${e.id}`}>
      <div><p className="text-[#0B192C]">{e.title || TYPE[e.type].label}</p><p className="text-xs text-slate-400">{fmtRange(e.start_date, e.end_date)}{e.provider ? ` · ${e.provider}` : ""} · <span className="uppercase">{e.status || "scheduled"}</span></p></div>
      <div className="flex items-center gap-3">{e.cost ? <span className="text-sm text-red-600">− {eur(e.cost)}</span> : null}<button onClick={() => del(e.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></div>
    </div>
  );
  return (
    <div data-testid={`management-${type}`}>
      <p className="eyebrow">Próximos de {label}</p>
      <div className="mt-4 space-y-2">{next.length ? next.map((e) => <Item key={e.id} e={e} />) : <p className="text-slate-400 text-sm">Nada programado. Añádelo desde la pestaña Calendario.</p>}</div>
      <p className="eyebrow mt-8">Historial de {label}</p>
      <div className="mt-4 space-y-2">{done.length ? done.map((e) => <Item key={e.id} e={e} />) : <p className="text-slate-400 text-sm">Sin registros.</p>}</div>
    </div>
  );
}

function CoOwners({ boatId, data, isAdmin, onChange }) {
  const [f, setF] = useState({ email: "", name: "" });
  const add = async () => {
    if (!f.email) return toast.error("Introduce un email");
    try { const { data: res } = await api.post(`/management/boats/${boatId}/members`, f); toast.success(res.user_exists ? "Invitación enviada al usuario existente" : "Invitación enviada para crear cuenta en Bevela"); setF({ email: "", name: "" }); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo invitar"); }
  };
  const remove = async (m) => { if (m.id) { await api.delete(`/management/boats/${boatId}/members/${m.id}`); onChange(); } };
  return (
    <div data-testid="management-coowners">
      <div className="space-y-2">
        {data.members.map((m, i) => (
          <div key={m.user_id || m.id || i} className="flex items-center justify-between rounded-xl border border-slate-100 p-4" data-testid={`coowner-${i}`}>
            <div><p className="text-[#0B192C]">{m.name} {m.role === "owner_admin" && <span className="text-[10px] uppercase tracking-widest text-[#C2A878] ml-1">Principal</span>}</p><p className="text-xs text-slate-400">{m.email || "—"} · {m.status === "active" ? "Activo" : "Invitado"}</p></div>
            {isAdmin && m.role !== "owner_admin" && (<button onClick={() => remove(m)} className="text-slate-300 hover:text-red-500" data-testid={`coowner-remove-${i}`}><Trash2 className="h-4 w-4" /></button>)}
          </div>
        ))}
      </div>
      {isAdmin && (
        <div className="card-clean p-5 mt-5" data-testid="coowner-add">
          <p className="eyebrow">Añadir copropietario</p>
          <div className="mt-3 grid sm:grid-cols-2 gap-4"><input className="input-elegant" placeholder="Email" value={f.email} onChange={(e) => setF((v) => ({ ...v, email: e.target.value }))} data-testid="coowner-email" /><input className="input-elegant" placeholder="Nombre (opcional)" value={f.name} onChange={(e) => setF((v) => ({ ...v, name: e.target.value }))} data-testid="coowner-name" /></div>
          <button onClick={add} className="btn-primary text-sm mt-4" data-testid="coowner-invite"><Plus className="h-4 w-4" /> Invitar</button>
          <p className="mt-2 text-xs text-slate-400">Se comprobará si el email existe en Bevela. La asociación no es definitiva hasta que se complete la validación.</p>
        </div>
      )}
    </div>
  );
}

function Documents({ boatId, docs, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); await api.post(`/management/boats/${boatId}/documents?kind=other`, fd); toast.success("Documento subido"); onChange(); }
    catch { toast.error("No se pudo subir"); } finally { setBusy(false); e.target.value = ""; }
  };
  const download = async (d) => {
    const res = await api.get(`/management/boats/${boatId}/documents/${d.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = d.filename; a.click(); URL.revokeObjectURL(url);
  };
  const del = async (d) => { await api.delete(`/management/boats/${boatId}/documents/${d.id}`); onChange(); };
  return (
    <div data-testid="management-documents">
      <label className="btn-outline text-sm cursor-pointer inline-flex" data-testid="doc-upload">
        <Upload className="h-4 w-4" /> {busy ? "Subiendo…" : "Subir documento"}
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={upload} disabled={busy} />
      </label>
      <div className="mt-5 space-y-2">
        {docs.length === 0 && <p className="text-slate-400 text-sm">Sin documentos. Sube seguros, facturas, ITB, etc.</p>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-4 rounded-xl border border-slate-100 p-4" data-testid={`doc-${d.id}`}>
            <FileText className="h-5 w-5 text-slate-400 shrink-0" />
            <div className="flex-1 min-w-0"><p className="text-[#0B192C] truncate">{d.filename}</p><p className="text-xs text-slate-400">{new Date(d.created_at).toLocaleDateString("es-ES")}</p></div>
            <button onClick={() => download(d)} className="text-slate-400 hover:text-[#0B192C]" data-testid={`doc-download-${d.id}`}><Download className="h-4 w-4" /></button>
            <button onClick={() => del(d)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
